import { useContext, useEffect, useState } from 'react'
import { Button, Container, Grid, Typography } from "@mui/material"
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { useGiftRepository } from '../../../../repositories/GiftSubscription/use-gift-repository'
import { Route, STRAPI_URL } from '../../../../router'
import { AppContext } from '../../../../stores/App/AppStore'
import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import MarkdownContent from '../../../atoms/MarkdownContent/MarkdownContent'
import Spacer from '../../../atoms/Spacer/Spacer'
import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"
import useGoogleTagManager from '../../../../helpers/analytics/use-google-tag-manager'
import GtmEventName from '../../../../helpers/analytics/GtmEventName'
import GiftRepeatableContent from "../../../molecules/GiftRepeatableContent/GiftRepeatableContent"
import BannerImage from '../../../atoms/BannerImage/BannerImage'
import LocalStorageKeys from '../../../../types/LocalStorageKeys'
import { logLandingPageAction } from '../../../../clients/logger'
import { MyInfoReason } from '../../../../types/MyInfoUserData'
import ConfirmationDialog from '../../../molecules/ConfirmationDialog/ConfirmationDialog'
import { useSubscriptionGiftRepository } from '../../../../repositories/SubscriptionGift/use-subscription-gift-repository'

const SubscriptionGiftRedemptionLandingPage = () => {
    useTrackPageView(`Gift Subscription Redemption Landing Page`)

    const { t } = useTranslation()
    const { code } = useParams()
    const navigate = useNavigate()
    const { trackButtonClick } = useGoogleTagManager()
    const { state: appState } = useContext(AppContext)
    const { retrieveGiftSubscriptionLandingPage } = useGiftRepository()
    const dialogErrorMessageTitle = t(`sign_up_failure`)
    const dialogErrorMessage = `Subscription Gift has been redeemed or is invalid.`
    const [isErrorDialogOpen, setIsErrorDialogOpen] = useState(false)
    const [giftCardURL, setGiftCardURL] = useState(``)
    const [giftMessage, setGiftMessage] = useState(``)
    const { retrieveSubscriptionGift } = useSubscriptionGiftRepository()

    useEffect(() => {
        if (!appState.pages.giftSubscriptionLandingPage.redemptionLandingBannerImageURL){
            retrieveGiftSubscriptionLandingPage()
        }
        if (code){
            retrieveSubscriptionGift(code ?? ``).then(subscriptionGift => {
                if (!subscriptionGift?.status || subscriptionGift?.status !== `PENDING_REDEMPTION`){
                    setIsErrorDialogOpen(true)

                }else {
                    setGiftCardURL(subscriptionGift?.giftCardImageURL ?? ``)
                    setGiftMessage(subscriptionGift?.recipientMessage ?? ``)
                    localStorage.setItem(LocalStorageKeys.SUBSCRIPTION_GIFT_REDEMPTION_CODE, code)
                }
            }).catch(() => {
                setIsErrorDialogOpen(true)
            })
        }
    }, [])

    const handleRegisterButton = () => {
        trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_REDEMPTION_LANDING_PAGE_SUBMIT)
        localStorage.setItem(LocalStorageKeys.MYINFO_REASON, MyInfoReason.SIGNUP)
        logLandingPageAction(`Sign Up`, `subscriptionGiftLanding`, code)
        window.location.href = `${STRAPI_URL}${Route.MYINFO_AUTHORISE}`
    }

    if (!code){
        return (<Container>
            <CommonBackButton path={Route.HOME} />
            <Typography variant="h4">Error Fetching Subscription Gift</Typography>
        </Container>
        )
    }

    const bannerImageURL = giftCardURL ?? appState.pages.giftSubscriptionLandingPage.redemptionLandingBannerImageURL

    return (
        <Container>
            <CommonBackButton path={Route.HOME} />
            <BannerImage imageUrl={bannerImageURL} className="App-logo" alt="logo" />
            <Spacer height={10}/>
            <Typography variant={`h6`}>
                {`Your sender's appreciation message: ` + giftMessage}
            </Typography>
            <Spacer height={20}/>
            <Typography variant={`h3`}>
                {appState.pages.giftSubscriptionLandingPage.redemptionLandingTitle}
            </Typography>
            <MarkdownContent content={appState.pages.giftSubscriptionLandingPage.redemptionLandingDescription} />
            <Spacer />
            <GiftRepeatableContent repeatableContent={appState.pages.giftSubscriptionLandingPage.redemptionLandingRepeatable ?? []}></GiftRepeatableContent>
            <Spacer height={15}/>
            <Grid>
                <Button variant='contained' onClick={handleRegisterButton} fullWidth>{t(`gift_subscription_landing_next`)}</Button>
            </Grid>
            <Spacer height={15}/>
            <ConfirmationDialog
                acknowledgeOnly
                buttonText={t(`close`)}
                confirmationTitle={dialogErrorMessageTitle}
                confirmationText={dialogErrorMessage}
                dismissButtonText={t(`common_back_to_home`)}
                defaultOpen={isErrorDialogOpen}
                onConfirm={() => navigate(Route.ROOT)}
            />
        </Container>
    )
}

export default SubscriptionGiftRedemptionLandingPage